import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Users, Clock, Check } from 'lucide-react';
import { Button } from './ui/Button';
interface Room {
  id: string;
  name: string;
  type: string;
  price: number;
  capacity: number;
}
interface BookingModalProps {
  room: Room | null;
  isOpen: boolean;
  onClose: () => void;
}
export function BookingModal({
  room,
  isOpen,
  onClose
}: BookingModalProps) {
  const [step, setStep] = useState(1);
  const [bookingData, setBookingData] = useState({
    checkIn: '',
    checkOut: '',
    guests: 1,
    specialRequests: ''
  });
  const [isConfirmed, setIsConfirmed] = useState(false);
  if (!room) return null;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsConfirmed(true);
    setTimeout(() => {
      setIsConfirmed(false);
      setStep(1);
      onClose();
    }, 3000);
  };
  const calculateNights = () => {
    if (!bookingData.checkIn || !bookingData.checkOut) return 0;
    const start = new Date(bookingData.checkIn);
    const end = new Date(bookingData.checkOut);
    const diff = end.getTime() - start.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };
  const nights = calculateNights();
  const subtotal = room.price * nights;
  const tax = subtotal * 0.1;
  const total = subtotal + tax;
  return <AnimatePresence data-id="element-85">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50" data-id="element-86" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.9,
        y: 20
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-87">
            <div className="bg-gradient-to-br from-[#1A2228] to-[#0F161B] border border-white/10 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl" data-id="element-88">
              {isConfirmed ?
          // Confirmation State
          <div className="p-12 flex flex-col items-center justify-center text-center" data-id="element-89">
                  <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 200,
              delay: 0.2
            }} className="w-24 h-24 bg-[#DDA247] rounded-full flex items-center justify-center mb-6" data-id="element-90">
                    <Check className="w-12 h-12 text-[#0B1014]" data-id="element-91" />
                  </motion.div>
                  <h3 className="text-3xl font-bold text-white mb-3" data-id="element-92">
                    Booking Confirmed!
                  </h3>
                  <p className="text-gray-400 max-w-md" data-id="element-93">
                    Your reservation for {room.name} has been confirmed. We've
                    sent a confirmation email with all the details.
                  </p>
                </div> : <>
                  {/* Header */}
                  <div className="flex items-center justify-between p-6 border-b border-white/10" data-id="element-94">
                    <div data-id="element-95">
                      <h2 className="text-2xl font-bold text-white uppercase tracking-wide" data-id="element-96">
                        {room.name}
                      </h2>
                      <p className="text-sm text-gray-400 mt-1" data-id="element-97">{room.type}</p>
                    </div>
                    <button onClick={onClose} className="w-10 h-10 bg-[#0B1014] rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-[#1A2228] transition-colors" data-id="element-98">
                      <X size={20} data-id="element-99" />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]" data-id="element-100">
                    <form onSubmit={handleSubmit} className="space-y-6" data-id="element-101">
                      {/* Date Selection */}
                      <div className="grid grid-cols-2 gap-4" data-id="element-102">
                        <div data-id="element-103">
                          <label className="block text-xs font-medium text-gray-400 uppercase tracking-widest mb-3" data-id="element-104">
                            <Calendar className="w-3 h-3 inline mr-2" data-id="element-105" />
                            Check-In
                          </label>
                          <input type="date" required value={bookingData.checkIn} onChange={e => setBookingData({
                      ...bookingData,
                      checkIn: e.target.value
                    })} className="w-full bg-[#0B1014] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#DDA247] focus:outline-none transition-colors [color-scheme:dark]" data-id="element-106" />
                        </div>
                        <div data-id="element-107">
                          <label className="block text-xs font-medium text-gray-400 uppercase tracking-widest mb-3" data-id="element-108">
                            <Calendar className="w-3 h-3 inline mr-2" data-id="element-109" />
                            Check-Out
                          </label>
                          <input type="date" required value={bookingData.checkOut} onChange={e => setBookingData({
                      ...bookingData,
                      checkOut: e.target.value
                    })} className="w-full bg-[#0B1014] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#DDA247] focus:outline-none transition-colors [color-scheme:dark]" data-id="element-110" />
                        </div>
                      </div>

                      {/* Guests */}
                      <div data-id="element-111">
                        <label className="block text-xs font-medium text-gray-400 uppercase tracking-widest mb-3" data-id="element-112">
                          <Users className="w-3 h-3 inline mr-2" data-id="element-113" />
                          Number of Guests
                        </label>
                        <input type="number" min="1" max={room.capacity} required value={bookingData.guests} onChange={e => setBookingData({
                    ...bookingData,
                    guests: parseInt(e.target.value)
                  })} className="w-full bg-[#0B1014] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#DDA247] focus:outline-none transition-colors" data-id="element-114" />
                        <p className="text-xs text-gray-600 mt-2" data-id="element-115">
                          Maximum capacity: {room.capacity} guests
                        </p>
                      </div>

                      {/* Special Requests */}
                      <div data-id="element-116">
                        <label className="block text-xs font-medium text-gray-400 uppercase tracking-widest mb-3" data-id="element-117">
                          Special Requests (Optional)
                        </label>
                        <textarea rows={4} value={bookingData.specialRequests} onChange={e => setBookingData({
                    ...bookingData,
                    specialRequests: e.target.value
                  })} placeholder="Any special requirements or requests..." className="w-full bg-[#0B1014] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:border-[#DDA247] focus:outline-none transition-colors resize-none" data-id="element-118" />
                      </div>

                      {/* Price Summary */}
                      {nights > 0 && <div className="bg-[#0B1014]/50 border border-white/5 rounded-xl p-6 space-y-3" data-id="element-119">
                          <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4" data-id="element-120">
                            Price Summary
                          </h3>
                          <div className="flex justify-between text-sm" data-id="element-121">
                            <span className="text-gray-400" data-id="element-122">
                              ${room.price.toLocaleString()} × {nights} night
                              {nights > 1 ? 's' : ''}
                            </span>
                            <span className="text-white font-medium" data-id="element-123">
                              ${subtotal.toLocaleString()}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm" data-id="element-124">
                            <span className="text-gray-400" data-id="element-125">Taxes & Fees</span>
                            <span className="text-white font-medium" data-id="element-126">
                              ${tax.toLocaleString()}
                            </span>
                          </div>
                          <div className="border-t border-white/10 pt-3 flex justify-between" data-id="element-127">
                            <span className="text-white font-bold" data-id="element-128">Total</span>
                            <span className="text-2xl font-bold text-[#DDA247]" data-id="element-129">
                              ${total.toLocaleString()}
                            </span>
                          </div>
                        </div>}

                      {/* Submit Button */}
                      <Button type="submit" variant="primary" size="lg" fullWidth data-id="element-130">
                        Confirm Booking
                      </Button>

                      <p className="text-xs text-gray-600 text-center" data-id="element-131">
                        By confirming, you agree to our terms and conditions
                      </p>
                    </form>
                  </div>
                </>}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}